import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Loader2, ChevronRight } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import CreateTeamForm from "../../components/CreateTeamForm";
import MemberSearch from "../../components/MemberSearch";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase";
import { getTeamIcon } from "../../components/crm/teams/teamTheme";

function ManageTeamsPage() {
  const { globalUser } = useAuth();
  const { teamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loadingTeam, setLoadingTeam] = useState(!!teamId);
  const [errorTeam, setErrorTeam] = useState(null);

  useEffect(() => {
    if (!teamId || !globalUser) {
      setSelectedTeam(null);
      setErrorTeam(null);
      setLoadingTeam(false);
      return;
    }

    const passedTeam = location.state?.team;
    if (passedTeam && passedTeam.id === teamId) {
      setSelectedTeam(passedTeam);
      setErrorTeam(null);
      setLoadingTeam(false);
      return;
    }

    const fetchTeamData = async () => {
      setLoadingTeam(true);
      setErrorTeam(null);
      try {
        const teamDocRef = doc(db, "teams", teamId);
        let snap = await getDoc(teamDocRef);
        if (!snap.exists()) {
          await new Promise((r) => setTimeout(r, 1000));
          snap = await getDoc(teamDocRef);
        }
        if (snap.exists()) {
          const teamData = { id: snap.id, ...snap.data() };
          if (teamData.ownerId === globalUser?.uid) {
            setSelectedTeam(teamData);
          } else {
            setErrorTeam("Access denied: you don't own this team.");
            setSelectedTeam(null);
            setTimeout(() => navigate("/portal/teams"), 2000);
          }
        } else {
          setErrorTeam("Team not found.");
          setTimeout(() => navigate("/portal/teams"), 2000);
        }
      } catch (err) {
        console.error("Error fetching team details:", err);
        setErrorTeam(`Failed to load team: ${err.message}`);
      } finally {
        setLoadingTeam(false);
      }
    };

    fetchTeamData();
  }, [teamId, globalUser, navigate, location.state]);

  const refreshTeamData = useCallback(async () => {
    if (!teamId) return;
    try {
      const snap = await getDoc(doc(db, "teams", teamId));
      if (snap.exists()) {
        setSelectedTeam({ id: snap.id, ...snap.data() });
        setErrorTeam(null);
      }
    } catch (err) {
      console.error("Error refreshing team data:", err);
    }
  }, [teamId]);

  const handleTeamCreated = () => {
    // Members are added inline on the create screen, so return to the overview.
    navigate("/portal/teams");
  };

  const TeamIcon = selectedTeam ? getTeamIcon(selectedTeam.teamIcon) : null;
  const accent = selectedTeam?.teamColor || "#EAB308";

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* ===== Create a new team ===== */}
        {!teamId && (
          <CreateTeamForm
            onTeamCreated={handleTeamCreated}
            onCancel={() => navigate("/portal/teams")}
          />
        )}

        {/* ===== Manage an existing team ===== */}
        {teamId && (
          <>
            {/* Breadcrumb + header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm">
                <button
                  type="button"
                  onClick={() => navigate("/portal/teams")}
                  className="text-slate-500 hover:text-slate-800"
                >
                  Teams
                </button>
                <ChevronRight className="h-4 w-4 text-slate-400" />
                <span className="font-medium text-slate-800">Manage Members</span>
              </div>
              <button
                type="button"
                onClick={() => navigate("/portal/teams")}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            </div>

            {loadingTeam && (
              <div className="flex h-[50vh] items-center justify-center text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}

            {!loadingTeam && errorTeam && (
              <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-center text-red-600">
                {errorTeam}
              </div>
            )}

            {!loadingTeam && !errorTeam && selectedTeam && (
              <>
                <div className="mt-4 flex items-center gap-3">
                  {TeamIcon && (
                    <span
                      className="flex h-11 w-11 items-center justify-center rounded-xl"
                      style={{ backgroundColor: `${accent}1A`, color: accent }}
                    >
                      <TeamIcon className="h-5 w-5" />
                    </span>
                  )}
                  <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                    Manage Members: {selectedTeam.teamName}
                  </h1>
                </div>

                <div className="mt-5">
                  <MemberSearch
                    team={selectedTeam}
                    onTeamUpdate={refreshTeamData}
                    onDone={() => navigate("/portal/teams")}
                  />
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default ManageTeamsPage;
